import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { analytics } from "../../analytics/analytics";
import { Button, PageHeader } from "../../components/ui";
import { t } from "../../i18n";
import { useAppStore } from "../../state/appStore";

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { anonymousSessionId, result, setAuthenticated, setResult } =
    useAppStore();
  const [step, setStep] = useState<"details" | "otp">("details");
  const [mobile, setMobile] = useState("+961 ");
  const [dob, setDob] = useState("");
  const [adult, setAdult] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);
  async function send() {
    setBusy(true);
    setError("");
    try {
      setChallenge(await api.startOtp(mobile, dob, adult));
      setStep("otp");
      setCountdown(30);
      const timer = setInterval(
        () =>
          setCountdown((v) => {
            if (v <= 1) {
              clearInterval(timer);
              return 0;
            }
            return v - 1;
          }),
        1000,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send code");
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    setBusy(true);
    setError("");
    try {
      await api.completeOtp(challenge, code, anonymousSessionId);
      setAuthenticated(true);
      analytics.track("auth_completed");
      if (result) {
        await api.claimPlans(result.requestId, anonymousSessionId);
        setResult(await api.getPlans(result.requestId));
        analytics.track("plan_claimed");
      }
      const returnTo = searchParams.get("returnTo");
      navigate(
        returnTo?.startsWith("/") && !returnTo.startsWith("//")
          ? returnTo
          : result
            ? `/plans/${result.plans[0]?.id ?? ""}`
            : "/",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="page auth-page">
      <PageHeader
        back={() => navigate(-1)}
        eyebrow="WELCOME"
        title={t.auth.title}
      />
      {step === "details" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            analytics.track("auth_started");
            void send();
          }}
        >
          <label>
            {t.auth.mobile}
            <input
              type="tel"
              inputMode="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              autoComplete="tel"
              required
            />
          </label>
          <label>
            {t.auth.dob}
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              required
            />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={adult}
              onChange={(e) => setAdult(e.target.checked)}
              required
            />
            <span>{t.auth.adult}</span>
          </label>
          <p className="legal-copy">
            By continuing, you agree to the Terms and acknowledge the Privacy
            Notice. Message rates may apply.
          </p>
          <Button type="submit" disabled={busy || !adult}>
            {busy ? "Sending…" : t.auth.send}
          </Button>
          <div className="divider">
            <span>or</span>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled
            title="Configure native Google client IDs to enable"
          >
            G&nbsp; {t.auth.google}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p>
            We sent a code to <strong>{mobile}</strong>.
          </p>
          <label>
            {t.auth.code}
            <input
              className="otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
            />
          </label>
          <Button type="submit" disabled={busy || code.length !== 6}>
            {busy ? "Verifying…" : t.auth.verify}
          </Button>
          <button
            type="button"
            className="text-button"
            disabled={countdown > 0}
            onClick={() => void send()}
          >
            {countdown ? `Resend in ${countdown}s` : "Resend code"}
          </button>
        </form>
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
